import { ExtendedPlatformContext } from '../../../../types/platformApi';
import { getAddUnifiedComponentsMethods } from './addComponents/addUnifiedComponents';
import { chainPromisesAsync } from './utils';
import { PageRef } from '@wix/document-services-types';
import {
  createUnifiedComponentsInstallationError,
  UnifiedComponentsInstallationErrorCode as ErrorType,
} from './errors';
import {
  AppData,
  UnifiedComponentsCollection,
  UnifiedPage,
  UnifiedWidget,
} from '../../../../types/unifiedComponents';
import { BIReporter } from './bi';
import { HOME_PAGE_ID } from '../../intentInstallService';

export function installUnifiedComponents(
  context: ExtendedPlatformContext,
  biReporter: BIReporter,
  appData: AppData,
  collection: UnifiedComponentsCollection,
) {
  const homePage = context.documentServices.pages.getReference(
    context.documentServices.homePage.get(),
  ) as PageRef;
  homePage.pageId = homePage.id;
  const { pages, widgets } = collection;
  const components_list = Object.keys(collection.pages).concat(
    Object.keys(collection.widgets),
  );
  const { addPage, addWidget } = getAddUnifiedComponentsMethods(
    context,
    appData,
  );

  function installUnifiedPages() {
    const addSinglePage = ([pageGuid, page]: [string, UnifiedPage]) =>
      new Promise<PageRef | undefined>((resolve, reject) =>
        addPage(page, widgets)
          .then(resolve)
          .catch((error) =>
            reject(
              createUnifiedComponentsInstallationError(
                ErrorType.addingUnifiedComponentFailed,
                `Failed to add '${page.base?.name}' page component`,
              )
                .withAppDefIds(appData.appDefinitionId)
                .withComponentGUID(pageGuid, 'unified-page')
                .withParentError(error),
            ),
          ),
      );

    return chainPromisesAsync(Object.entries(pages), addSinglePage);
  }

  function checkIfShouldSkipWidget(widget: UnifiedWidget) {
    if (!context.intentInstallServices.shouldUseWidgetAppsConfiguration()) {
      return false;
    }

    const widgetPages = context.intentInstallServices.getWidgetsConfiguration();

    const widgetHasPageSelected = !!widgetPages[widget.widgetId];
    const isEssentialWidget = widget.installation?.base?.essential;

    if (!widgetHasPageSelected && !isEssentialWidget) {
      return true;
    }

    return false;
  }

  function getPageRefForWidget(widget: UnifiedWidget) {
    if (!context.intentInstallServices.shouldUseWidgetAppsConfiguration()) {
      return homePage;
    }

    const pagesData = context.documentServices.pages.getPagesData();

    const widgetPages = context.intentInstallServices.getWidgetsConfiguration();

    const widgetPage = pagesData.find(
      (page) => page.tpaPageId === widgetPages[widget.widgetId],
    )?.id;

    const pageRef =
      widgetPage && widgetPage !== HOME_PAGE_ID
        ? (context.documentServices.pages.getReference(widgetPage) as PageRef)
        : homePage;

    return pageRef;
  }

  function addSingleWidget(widget: UnifiedWidget) {
    if (checkIfShouldSkipWidget(widget)) {
      return Promise.resolve();
    }

    const pageRef = getPageRefForWidget(widget);

    return new Promise<PageRef | void>((resolve, reject) =>
      addWidget(pageRef, widget)
        .then(resolve)
        .catch((error) =>
          reject(
            createUnifiedComponentsInstallationError(
              ErrorType.addingUnifiedComponentFailed,
              `Failed to add '${widget?.base?.name}' widget component`,
            )
              .withAppDefIds(appData.appDefinitionId)
              .withComponentGUID(widget.widgetId, 'unified-widget')
              .withParentError(error),
          ),
        ),
    );
  }

  function installUnifiedWidgets() {
    return new Promise<void>((resolve, reject) => {
      const widgetsArray = Object.values(widgets);
      if (widgetsArray.length === 0) {
        return resolve();
      }

      chainPromisesAsync(widgetsArray, addSingleWidget)
        .then(resolve)
        .catch(reject);
    });
  }

  return biReporter.withBI(
    'NEW_COMPONENTS_INSTALL',
    {
      components_list,
      numberInstalledComponents: 0,
      appDefinitionId: appData.appDefinitionId,
    },
    function addUnifiedComponentsToStage() {
      return new Promise<void>((resolve, reject) =>
        installUnifiedPages()
          .then(installUnifiedWidgets)
          .then(resolve)
          .catch(reject),
      );
    },
  );
}
